import { useQuery } from "@apollo/client/react";
import { Badge, BadgeIcon, BadgeText } from "@repo/components/src/ui/badge";
import { Box } from "@repo/components/src/ui/box";
import { Card } from "@repo/components/src/ui/card";
import { Heading } from "@repo/components/src/ui/heading";
import { HStack } from "@repo/components/src/ui/hstack";
import { Image } from "@repo/components/src/ui/image";
import { Skeleton } from "@repo/components/src/ui/skeleton";
import { Text } from "@repo/components/src/ui/text";
import { formatNumber } from "@repo/components/src/utils/numbers";
import { UserReviewDocument } from "@repo/graphql/documents/review/query.generated";
import dayjs from "dayjs";
import { StarIcon } from "lucide-react-native";
import React from "react";
import { GetUserProfileQuery } from "@repo/graphql/documents/user/query.generated";

type Props = {
  userId: string;
  user: GetUserProfileQuery["userResource"]["get"];
};

const SharedProfileReviews = React.memo((props: Props) => {
  const { data, loading } = useQuery(UserReviewDocument, {
    variables: { userId: props.userId },
  });
  const isLoading = loading;
  const userFields = props.user;
  if (data?.review.userReview.totalCalifications === 0)
    return (
      <Text action="muted">
        {userFields.firstName} aún no tiene reviews en Montu.
      </Text>
    );
  return (
    <HStack flexSpace="md">
      <Box className="w-1/2">
        <Skeleton variant="rounded" isLoaded={!isLoading} className="h-12">
          <Card
            variant="outline"
            size="lg"
            className="w-full rounded-xl h-full"
          >
            <Heading bold numberOfLines={1}>
              Reviews totales
            </Heading>
            <Text size="2xl" className="mt-3">
              {formatNumber(data?.review.userReview.totalCalifications ?? 0)}
            </Text>
            <Text size="sm" action="muted">
              De {formatNumber(data?.review.userReview.totalBrands ?? 0)} marcas
              con las que colaboró.
            </Text>
          </Card>
        </Skeleton>
      </Box>
      <Box className="w-1/2">
        <Skeleton variant="rounded" isLoaded={!isLoading} className="h-12">
          <Card
            variant="outline"
            size="lg"
            className="w-full rounded-xl h-full"
          >
            <Heading bold numberOfLines={1}>
              Calificación
            </Heading>
            <Text size="2xl" className="mt-3">
              {formatNumber(data?.review.userReview.average ?? 0)} de 5
            </Text>
            <Text size="sm" action="muted">
              Promedio de sus reviews.
            </Text>
          </Card>
        </Skeleton>
      </Box>
      <Box className="w-full">
        <Skeleton isLoaded={!isLoading} className="h-24">
          <Card variant="outline" size="lg" className="w-full rounded-xl">
            <Heading bold numberOfLines={1}>
              Reviews
            </Heading>
            <HStack flexSpace="md" className="mt-2">
              {data?.review.userReview.lastReviews.map((review) => {
                const picture = review.pictureUrl;
                return (
                  <Box key={review.id} className="w-1/3">
                    <Box className="p-4 shadow-md rounded-lg h-full">
                      <HStack space="md">
                        <Heading
                          className="flex-1 shrink font-semibold"
                          numberOfLines={1}
                          size="sm"
                        >
                          {review.reviewer}
                        </Heading>
                        <Badge
                          action="muted"
                          className="border gap-x-1 items-center"
                          size="sm"
                        >
                          <BadgeText>{review.calification} de 5</BadgeText>
                          <BadgeIcon as={StarIcon} />
                        </Badge>
                      </HStack>
                      <Text size="sm" action="muted" className="mt-1">
                        {dayjs(review.timestamp).calendarFormat("DD-MM-YYYY")}
                      </Text>
                      <Text className="mt-2 flex-1 shrink" numberOfLines={4}>
                        {review.comment}
                      </Text>
                      <Image
                        size="none"
                        className="w-full aspect-square rounded-lg mt-6"
                        source={{ uri: picture?.url }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </HStack>
          </Card>
        </Skeleton>
      </Box>
    </HStack>
  );
});

SharedProfileReviews.displayName = "SharedProfileReviews";

export default SharedProfileReviews;
